import { useState } from "react";

import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";

import Button from "@material-ui/core/Button";

import { Grid, MenuItem, TextField } from "@material-ui/core";
import { Divider } from "@mui/material";
import { useGlobal } from "../../contexts/globalContext";
import { validateAllRooms } from "../../utils/validator";
import QuantitySelector from "../QuantitySelector";
import * as S from "./styles";

function ChoosePackageRooms({
  open,
  onClose,
  onConfirm,
  initialRooms = 1,
  initialAdults = 1,
  initialChildren = 0,
}) {
  const { showAlert } = useGlobal();
  const [apartments, setApartments] = useState([
    {
      adults: initialAdults,
      seniors: 0,
      seniorsAges: [],
      childs: initialChildren,
      childsAge: [],
    },
  ]);

  const getRoomTotal = (apartment) => {
    return apartment.adults + apartment.seniors + apartment.childs;
  };

  const handleAddApartment = () => {
    setApartments((prevApartments) => [
      ...prevApartments,
      {
        adults: 1,
        seniors: 0,
        seniorsAges: [],
        childs: 0,
        childsAge: [],
      },
    ]);
  };

  const handleRemoveLastApartment = () => {
    if (apartments.length > 1) {
      setApartments((prevApartments) => prevApartments.slice(0, -1));
    }
  };

  const handleConfirm = () => {
    const validation = validateAllRooms(apartments);

    if (!validation.valid) {
      showAlert(
        "error",
        `O apartamento ${validation.roomIndex + 1} possui ${
          validation.totalGuests
        } hóspedes, mas o máximo permitido é de 4 pessoas por quarto. Por favor, ajuste a quantidade de hóspedes.`
      );
      return; // Impede fechamento do modal
    }

    const totalAdults = apartments.reduce(
      (sum, apt) => sum + apt.adults + apt.seniors,
      0
    );
    const totalChildren = apartments.reduce((sum, apt) => sum + apt.childs, 0);
    const totalRooms = apartments.length;

    const childrenAges = apartments.flatMap((apt) => apt.childsAge);

    onConfirm({
      qtRooms: totalRooms,
      qtAdult: totalAdults,
      qtChild: totalChildren,
      childAge: childrenAges,
      apartments: apartments,
    });

    onClose(false);
  };

  const childAgeOptions = Array.from({ length: 18 }, (_, i) => i).map(
    (age) => ({
      label: age,
      value: age,
    })
  );

  const seniorAgeOptions = Array.from({ length: 40 }, (_, i) => i + 60).map(
    (age) => ({
      label: age,
      value: age,
    })
  );

  return (
    <S.Modal>
      <Dialog
        maxWidth="xs"
        open={open}
        onClose={() => {
          onClose(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent dividers>
          <Grid container direction="column" spacing={2}>
            <Grid item xs={12}>
              <Grid direction="row" container spacing={2} alignItems="center">
                <Grid item xs={6}>
                  <S.Title>Apartamentos</S.Title>
                </Grid>
                <Grid item xs={6}>
                  <QuantitySelector
                    count={apartments.length}
                    handleIncrement={handleAddApartment}
                    handleDecrement={handleRemoveLastApartment}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Divider style={{ marginBottom: "16px" }} />
            <Grid container direction="column" spacing={2}>
              {apartments.map((apartment, index) => {
                const roomTotal = getRoomTotal(apartment);
                const isAtLimit = roomTotal >= 4;
                const isNearLimit = roomTotal === 3;

                return (
                  <Grid key={index} item xs={12}>
                    <S.Title
                      style={{
                        fontWeight: "bold",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      Apartamento {index + 1}
                      <span
                        style={{
                          fontSize: "0.85rem",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          backgroundColor: isAtLimit
                            ? "#f44336"
                            : isNearLimit
                            ? "#ff9800"
                            : "#4caf50",
                          color: "white",
                        }}
                      >
                        {roomTotal}/4 hóspedes
                      </span>
                    </S.Title>
                    <Grid container direction="row" alignItems="center">
                      <Grid item xs={6}>
                        <S.Title>Adultos</S.Title>
                      </Grid>
                      <Grid item xs={6}>
                        <QuantitySelector
                          count={apartment.adults}
                          handleIncrement={() => {
                            const newApartments = [...apartments];
                            const currentTotal = getRoomTotal(
                              newApartments[index]
                            );

                            if (currentTotal >= 4) {
                              showAlert(
                                "warning",
                                `O apartamento ${
                                  index + 1
                                } já possui o máximo de 4 hóspedes permitidos por quarto.`
                              );
                              return;
                            }

                            newApartments[index].adults += 1;
                            setApartments(newApartments);
                          }}
                          handleDecrement={() => {
                            const newApartments = [...apartments];
                            if (newApartments[index].adults > 1) {
                              newApartments[index].adults -= 1;
                              setApartments(newApartments);
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <S.Title>Idosos (60-99) </S.Title>
                      </Grid>
                      <Grid item xs={6}>
                        <QuantitySelector
                          AllowedZero
                          count={apartment.seniors}
                          handleIncrement={() => {
                            const newApartments = [...apartments];
                            const currentTotal = getRoomTotal(
                              newApartments[index]
                            );

                            if (currentTotal >= 4) {
                              showAlert(
                                "warning",
                                `O apartamento ${
                                  index + 1
                                } já possui o máximo de 4 hóspedes permitidos por quarto.`
                              );
                              return;
                            }

                            newApartments[index].seniors += 1;
                            setApartments(newApartments);
                          }}
                          handleDecrement={() => {
                            const newApartments = [...apartments];
                            if (newApartments[index].seniors > 0) {
                              newApartments[index].seniors -= 1;
                              setApartments(newApartments);
                            }
                          }}
                        />
                      </Grid>
                      {apartment.seniors > 0 && (
                        <Grid item xs={12}>
                          <Grid container direction="row" spacing={2}>
                            {Array.from({ length: apartment.seniors }).map(
                              (_, seniorIndex) => (
                                <Grid item key={seniorIndex}>
                                  <TextField
                                    select
                                    variant="outlined"
                                    value={
                                      apartment.seniorsAges[seniorIndex] || ""
                                    }
                                    onChange={(e) => {
                                      const newApartments = [...apartments];
                                      const age = e.target.value;
                                      if (age >= 60 && age <= 99) {
                                        newApartments[index].seniorsAges[
                                          seniorIndex
                                        ] = age;
                                        setApartments(newApartments);
                                      }
                                    }}
                                  >
                                    {seniorAgeOptions.map((option) => (
                                      <MenuItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </Grid>
                              )
                            )}
                          </Grid>
                        </Grid>
                      )}
                      <Grid item xs={6}>
                        <S.Title>Criança (0-17 anos)</S.Title>
                      </Grid>
                      <Grid item xs={6}>
                        <QuantitySelector
                          AllowedZero
                          count={apartment.childs}
                          handleIncrement={() => {
                            const newApartments = [...apartments];
                            const currentTotal = getRoomTotal(
                              newApartments[index]
                            );

                            if (currentTotal >= 4) {
                              showAlert(
                                "warning",
                                `O apartamento ${
                                  index + 1
                                } já possui o máximo de 4 hóspedes permitidos por quarto.`
                              );
                              return;
                            }

                            newApartments[index].childs += 1;
                            setApartments(newApartments);
                          }}
                          handleDecrement={() => {
                            const newApartments = [...apartments];
                            if (newApartments[index].childs > 0) {
                              newApartments[index].childs -= 1;
                              setApartments(newApartments);
                            }
                          }}
                        />
                      </Grid>
                      {apartment.childs > 0 && (
                        <Grid item>
                          <Grid container direction="row" spacing={2}>
                            {Array.from({ length: apartment.childs }).map(
                              (_, childIndex) => (
                                <Grid item key={childIndex}>
                                  <TextField
                                    select
                                    variant="outlined"
                                    value={
                                      apartment.childsAge[childIndex] || ""
                                    }
                                    onChange={(e) => {
                                      const newApartments = [...apartments];
                                      const age = e.target.value;
                                      if (age >= 0 && age <= 17) {
                                        newApartments[index].childsAge[
                                          childIndex
                                        ] = age;
                                        setApartments(newApartments);
                                      }
                                    }}
                                  >
                                    {childAgeOptions.map((option) => (
                                      <MenuItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </Grid>
                              )
                            )}
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              onClose(false);
            }}
            color="primary"
          >
            Voltar
          </Button>
          <Button onClick={handleConfirm} color="primary" variant="contained">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </S.Modal>
  );
}

export default ChoosePackageRooms;
